import React, { useState, useEffect } from 'react';
import { useDhis2ConnectionStatus } from '@dhis2/app-runtime';
import { Button, InputField, AlertBar, NoticeBox } from '@dhis2/ui';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai'; // Import the react icons

const Login = () => {
  const { isAuthorized } = useDhis2ConnectionStatus();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); // New state for password visibility
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();

  const showNotification = (type, message, details = null) => {
    setNotification({ type, message, details });
    setTimeout(() => setNotification(null), 5000);
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Redirect if user exists (authenticated)
  useEffect(() => {
    if (user && !authLoading) {
      console.log('User authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Show loading while checking initial auth state
  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '80vh' 
      }}>
        <div>Checking authentication...</div>
      </div>
    );
  }

  // If user exists and not loading, redirect
  if (user && !authLoading) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotification(null);

    try {
      if (!username.trim() || !password.trim()) {
        throw new Error('Please enter both username and password');
      }

      const success = await login(username, password);
      
      if (success) {
        showNotification('success', 'Login successful!', 'Redirecting to dashboard...');
        // The useEffect will handle the redirect when user state updates
      } else {
        throw new Error('Login failed. Please check your credentials.');
      }

    } catch (err) {
      showNotification('error', 'Login failed', err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderFormField = (label, children, required = true) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontWeight: '500', marginBottom: '4px' }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </div>
      {children}
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        padding: '1rem',
        backgroundColor: '#f7f9fc',
      }}
    >
      <form
        onSubmit={handleLogin}
        style={{
          backgroundColor: 'white',
          padding: '2rem 2.5rem',
          borderRadius: '10px',
          boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
          maxWidth: '400px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
        aria-label="Login form"
      >
        <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Welcome to Pannecia PMS</h2>
        
        {/* Notification Alert */}
        {notification && (
          <div style={{ marginBottom: '1rem' }}>
            <AlertBar 
              {...(notification.type === 'success' ? { success: true } : 
                   notification.type === 'info' ? { info: true } : 
                   { critical: true })}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 'bold' }}>{notification.message}</span>
                {notification.details && (
                  <span style={{ marginTop: '4px', fontSize: '0.9em' }}>
                    {notification.details}
                  </span>
                )}
              </div>
            </AlertBar>
          </div>
        )}
        
        {renderFormField('Username', (
          <InputField
            value={username}
            onChange={({ value }) => setUsername(value)}
            required
            disabled={loading}
            dense
            autoComplete="username"
            data-test="login-username"
          />
        ))}
        
        {renderFormField('Password', (
          <div style={{ position: 'relative' }}>
            <InputField
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={({ value }) => setPassword(value)}
              required
              disabled={loading}
              dense
              autoComplete="current-password"
              data-test="login-password"
              style={{ width: '100%', paddingRight: '40px' }} // Add padding for the icon
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
                color: '#555',
                fontSize: '20px',
              }}
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  togglePasswordVisibility();
                  e.preventDefault();
                }
              }}
            >
              {showPassword ? (
                <AiFillEyeInvisible />
              ) : (
                <AiFillEye />
              )}
            </button>
          </div>
        ))}

        <Button
          type="submit"
          primary
          loading={loading}
          disabled={loading || !username.trim() || !password.trim()}
          style={{ 
            marginBottom: '1.5rem', 
            fontWeight: '600', 
            fontSize: '1rem', 
            padding: '0.75rem 0' 
          }}
          data-test="login-button"
        >
          {loading ? 'Logging in...' : 'Login'}
        </Button>

        <div
          style={{
            textAlign: 'center',
            borderTop: '1px solid #e0e0e0',
            paddingTop: '1rem',
            fontSize: '0.9rem',
            color: '#555',
          }}
        >
          <span style={{ marginRight: '6px' }}>Don't have an account?</span>
          <Link
            to="/register"
            style={{
              cursor: 'pointer',
              fontWeight: '600',
              color: '#0075db',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
          >
            Register here
          </Link>
        </div>

        <div style={{ 
          marginTop: '1rem',
          textAlign: 'center',
          fontSize: '0.8rem',
        }}>
          <Link
            to="/forgot-password"
            style={{
              color: '#666',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
          >
            Forgot your password?
          </Link>
        </div>
      </form>
    </div>
  )
};

export default Login;